// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { checkAuthentication, getLastAdvisorStatus } from '../utils/auth';
import { SplashScreen } from './SplashScreen';

type AuthenticatedRouteProps = Readonly<{
  children: React.ReactNode;
}>;

/**
 * AuthenticatedRoute - Simple auth wrapper without Layout
 *
 * Use this for protected routes that need authentication
 * but should NOT render inside the main Layout with sidebar/header.
 *
 * Examples: Registration completion page, standalone protected pages
 */
export function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [showSplash, setShowSplash] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const authenticated = await checkAuthentication();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Error verifying authentication:', error);
        setIsAuthenticated(false);
      } finally {
        setIsChecking(false);
      }
    };

    verifyAuth();
  }, []);

  useEffect(() => {
    if (!isChecking) {
      const timer = setTimeout(() => setShowSplash(false), 1500);
      return () => clearTimeout(timer);
    }
  }, [isChecking]);

  if (isChecking || showSplash) {
    return <SplashScreen message="Verifying authentication..." />;
  }

  if (!isAuthenticated) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  // SSO-provisioned advisors at brokerages with REQUIRE_ADMIN_APPROVAL land
  // here on first sign-in. Their session is valid but they can't use the
  // platform until an admin activates them.
  if (getLastAdvisorStatus() === 'pending_activation'
      && location.pathname !== '/awaiting-approval') {
    return <Navigate to="/awaiting-approval" replace />;
  }

  return <>{children}</>;
}
